import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchTrending } from "../api/client";
import RecoveryPanel from "../components/RecoveryPanel";
import ShowCard from "../components/ShowCard";
import Badge from "../components/ui/Badge";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import Chip from "../components/ui/Chip";
import EmptyState from "../components/ui/EmptyState";
import LoadingState from "../components/ui/LoadingState";
import { showPath } from "../utils/showDisplay";

export default function Trending() {
  const [shows, setShows] = useState([]);
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("Loading trending titles...");
  const [reloadAttempt, setReloadAttempt] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const loadTrending = async () => {
      try {
        setStatus("loading");
        setMessage("Loading trending titles...");

        const response = await fetchTrending(5);
        const results = response?.data;

        if (!isMounted) return;

        if (!Array.isArray(results)) {
          setShows([]);
          setStatus("malformed");
          setMessage("Trending returned an unexpected response.");
          return;
        }

        setShows(results);
        setStatus(results.length ? "success" : "empty");
        setMessage(
          results.length
            ? ""
            : "No trending titles are available yet. Check back after feedback activity is recorded."
        );
      } catch (err) {
        if (!isMounted) return;

        setShows([]);
        setStatus("error");
        setMessage(err?.response?.data?.detail || "Trending could not load.");
      }
    };

    loadTrending();

    return () => {
      isMounted = false;
    };
  }, [reloadAttempt]);

  const retryTrending = () => {
    setReloadAttempt((attempt) => attempt + 1);
  };

  const hasRecoveryError = status === "error" || status === "malformed";

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8 grid gap-6 lg:grid-cols-[1fr_340px] lg:items-center">
        <div>
          <Badge variant="gold" className="mb-5">Real feedback</Badge>
          <h1 className="font-display text-4xl font-bold leading-tight text-hookd-cream sm:text-5xl">
            What's trending with the culture right now.
          </h1>
          <p className="mt-3 max-w-2xl text-hookd-muted">
            Real vibes. Real feedback. Real recommendations.
          </p>
        </div>

        <Card className="p-5">
          <h2 className="font-bold text-hookd-cream">How Trending Works</h2>
          <p className="mt-2 text-sm leading-6 text-hookd-muted">
            Loved feedback lifts a title. Meh holds steady. Nope pushes it down.
          </p>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center text-sm">
            <span className="rounded-hookd border border-green-400/30 bg-green-400/10 p-2 text-green-300">Love +1</span>
            <span className="rounded-hookd border border-hookd-gold/30 bg-hookd-gold/10 p-2 text-hookd-gold">Meh 0</span>
            <span className="rounded-hookd border border-hookd-neon/30 bg-hookd-neon/10 p-2 text-hookd-neon">Nope -1</span>
          </div>
        </Card>
      </div>

      <div className="mb-6 flex flex-wrap gap-3">
        <Chip active>All Trending</Chip>
        <Chip>This Week</Chip>
        <Chip>All Genres</Chip>
        <Chip>All Platforms</Chip>
      </div>

      {status === "loading" && (
        <LoadingState title="Loading trending titles" message={message} />
      )}

      {hasRecoveryError && (
        <RecoveryPanel
          title={status === "malformed" ? "Trending could not be read" : "Trending did not load"}
          message={message}
          variant="error"
          actions={[
            { label: "Try Again", onClick: retryTrending },
            { label: "Take the Quiz", to: "/quiz" },
          ]}
        />
      )}

      {status !== "loading" && !hasRecoveryError && shows.length === 0 && (
        <EmptyState
          title="No trending titles yet"
          message={message}
          primaryAction={{ label: "Take the Quiz", to: "/quiz" }}
        />
      )}

      {shows.length > 0 && (
        <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
          <div className="grid gap-4">
            {shows.map((show, index) => (
              <ShowCard
                key={show.title}
                show={show}
                rank={index + 1}
                detailsTo={showPath(show)}
              />
            ))}
          </div>

          <aside className="grid content-start gap-5">
            <Card className="p-5">
              <h2 className="font-display text-2xl font-bold text-hookd-cream">
                Trending by Vibe
              </h2>
              <div className="mt-5 space-y-4">
                {["High Energy", "Heartfelt", "Fun & Playful", "Deep & Thoughtful"].map((label, index) => (
                  <div key={label}>
                    <div className="mb-2 flex justify-between text-sm">
                      <span>{label}</span>
                      <span className="text-hookd-muted">{shows.length - index}</span>
                    </div>
                    <div className="h-2 rounded-full bg-white/10">
                      <div className="h-2 rounded-full bg-hookd-purple" style={{ width: `${85 - index * 14}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-5">
              <h2 className="font-display text-2xl font-bold text-hookd-cream">
                Don't see your vibe?
              </h2>
              <p className="mt-2 text-sm leading-6 text-hookd-muted">
                Retake the quiz to get a fresh set of picks.
              </p>
              <Button as={Link} to="/quiz" className="mt-5 w-full">Retake the Vibe Quiz</Button>
            </Card>
          </aside>
        </div>
      )}
    </div>
  );
}
